import type {
  ServerBaggageAvailabilityDefinition,
  ServerBaggageAvailabilityOffer,
  ServerBaggageAvailabilityResponse,
} from 'api/flights'

// Folds the raw `{ offerId, definitions[], offers[] }` availability response into a
// per-flight, per-traveller structure the baggage modal can render directly, while keeping
// everything the selection→`AncillariesForReservation` builder needs (offerId, subCode,
// airline, basePriceAmount, currency). Mirrors `seatsAvailabilityMap` in style.
export function baggageAvailabilityMap(
  serverResponse: ServerBaggageAvailabilityResponse,
): App.FlightsBaggageAvailability {
  const definitionsById = new Map<string, ServerBaggageAvailabilityDefinition>(
    (serverResponse.definitions ?? []).map(definition => [definition.id, definition]),
  )

  // flightId -> travellerId -> options, preserving first-seen ordering.
  const flightOrder: Array<string> = []
  const flightsById = new Map<string, App.FlightsBaggageAvailabilityFlight>()

  let currency = ''

  for (const offer of serverResponse.offers ?? []) {
    const definition = definitionsById.get(offer.ancillaryDefinitionId)
    const option = mapOption(offer, definition)
    // Hide options with no KILO weight limit: the modal shows weight-only labels, so a
    // definition without a KILO weight has nothing meaningful to display.
    if (option.kg === undefined) { continue }
    if (option.currency && !currency) {
      currency = option.currency
    }

    let flight = flightsById.get(offer.flightId)
    if (!flight) {
      flight = {
        flightId: offer.flightId,
        origin: offer.origin,
        destination: offer.destination,
        travellers: [],
      }
      flightsById.set(offer.flightId, flight)
      flightOrder.push(offer.flightId)
    }

    for (const travellerId of offer.travelerIds ?? []) {
      let traveller = flight.travellers.find(entry => entry.travellerId === travellerId)
      if (!traveller) {
        traveller = { travellerId, options: [] }
        flight.travellers.push(traveller)
      }
      // Each offer/traveller pairing carries its own price, so clone per traveller.
      traveller.options.push({ ...option })
    }
  }

  return {
    offerId: serverResponse.offerId,
    currency,
    flights: flightOrder.map(flightId => flightsById.get(flightId) as App.FlightsBaggageAvailabilityFlight),
  }
}

function mapOption(
  offer: ServerBaggageAvailabilityOffer,
  definition: ServerBaggageAvailabilityDefinition | undefined,
): App.FlightsBaggageAvailabilityOption {
  const total = offer.price?.total ?? offer.price?.base
  const base = offer.price?.base ?? offer.price?.total
  const kg = getKiloWeight(definition)
  const subCode = definition?.subCode ?? ''

  return {
    id: offer.offerItemId ?? `${offer.flightId}:${subCode}`,
    definitionId: offer.ancillaryDefinitionId,
    subCode,
    airline: definition?.airline ?? '',
    label: kg !== undefined ? `${kg}kg` : '',
    kg,
    amount: total?.amount ?? 0,
    basePriceAmount: base?.amount ?? 0,
    currency: total?.currency ?? base?.currency ?? '',
    displayOnly: offer.price?.displayOnly ?? false,
    unavailable: offer.price?.unavailable ?? false,
  }
}

function getKiloWeight(definition: ServerBaggageAvailabilityDefinition | undefined): number | undefined {
  const kiloLimit = definition?.weightLimits?.find(limit => limit.unit === 'KILO')
  return kiloLimit?.weight
}
